import { GridColDef, GridColumnVisibilityModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { LS_KEYS } from '../constants';
import { useLocation } from 'react-router';
import usePersist from './usePersist';

type Props<R extends GridValidRowModel> = {
  manualLSKey?: string;
  columns: readonly GridColDef<R>[];
  defaultVisibleColumns: GridColumnVisibilityModel;
};

const useVisibleColumns = <R extends GridValidRowModel>({ manualLSKey, columns, defaultVisibleColumns }: Props<R>) => {
  const [visibleColumns, setVisibleColumns] = useState<GridColumnVisibilityModel>({});
  const { pathname } = useLocation();
  const visibleColumnsLSKey = manualLSKey ?? `${LS_KEYS.visibleColumns}-${pathname}`;
  const { getPersistData, setPersistData } = usePersist(false);

  const visibleColumnsLS = getPersistData<GridColumnVisibilityModel>(visibleColumnsLSKey);

  const getVisibilityModel = (savedColumns?: GridColumnVisibilityModel) =>
    columns.reduce<GridColumnVisibilityModel>((acc, column) => {
      return {
        ...acc,
        [column.field]: savedColumns?.[column.field] ?? defaultVisibleColumns[column.field],
      };
    }, {});

  // set visible columns from local storage or based on defaultVisibleColumns
  useEffect(() => {
    if (!isEmpty(visibleColumnsLS)) {
      const visibleColumnsWithDefaults = getVisibilityModel(visibleColumnsLS);
      setVisibleColumns(visibleColumnsWithDefaults);
      setPersistData<GridColumnVisibilityModel>(visibleColumnsLSKey, visibleColumnsWithDefaults);
    } else {
      const visibleColumnsInitial = getVisibilityModel();

      setVisibleColumns(visibleColumnsInitial);
      setPersistData<GridColumnVisibilityModel>(visibleColumnsLSKey, visibleColumnsInitial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  return {
    visibleColumns,
    setVisibleColumns,
    visibleColumnsLSKey,
  };
};

export default useVisibleColumns;
